// Peças comuns das telas de pagamento do Reservei (2026-10-20): compra
// (ReserveiCheckout), troca de plano (ReserveiPlanChange) e pagar fatura
// (ReserveiChargeCheckout). Mesmo topo, rodapé, preço, regras de cartão e QR —
// o cliente não pode sentir que mudou de produto entre uma tela e outra.

import { motion } from 'framer-motion';
import { ArrowRight, Check, CircleHelp, Copy } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatBRDateTime, todayLocal } from '@control-center/shared';

import './reservei-checkout.css';

export const RESERVEI_FALLBACK_URL = 'https://reservei.app.br';

/** Mesmo corte do CSS (`@media (min-width: 1041px)` em reservei-checkout.css). */
export const DESKTOP_QUERY = '(min-width: 1041px)';

export const EASE = [0.22, 1, 0.36, 1] as const;

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export function formatPrice(value: number): string {
  return BRL.format(value);
}

/** Valor positivo vindo da API (string decimal) — senão null. */
export function toAmount(value: string | null | undefined): number | null {
  if (value == null) return null;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** "Reservei Essencial" → "Essencial": a página já é do Reservei. */
export function displayPlanName(name: string): string {
  const short = name.replace(/^reservei\s+/i, '').trim();
  return short.length > 0 ? short : name;
}

export const onlyDigits = (value: string): string => value.replace(/\D/g, '');

export function formatCardNumber(value: string): string {
  return onlyDigits(value).slice(0, 19).replace(/(.{4})/g, '$1 ').trim();
}

export function formatExpiry(value: string): string {
  const digits = onlyDigits(value).slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
}

/** Validade MM/AA ainda vigente no mês corrente (fuso BR). */
export function isExpiryValid(value: string): boolean {
  const match = /^(\d{2})\/(\d{2})$/.exec(value);
  if (!match) return false;
  const month = Number(match[1]);
  const year = 2000 + Number(match[2]);
  if (month < 1 || month > 12) return false;
  const [currentYear = 0, currentMonth = 0] = todayLocal().split('-').map(Number);
  return year > currentYear || (year === currentYear && month >= currentMonth);
}

/** "12/30" → { month: '12', year: '2030' } (o formato que a API do cartão pede). */
export function splitExpiry(value: string): { month: string; year: string } {
  const [month = '', year = ''] = value.split('/');
  return { month, year: year ? `20${year}` : '' };
}

/**
 * O Asaas manda a validade do QR como "2026-10-23 23:59:59", sem fuso — é
 * horário de Brasília (sem horário de verão desde 2019). Ler como hora local do
 * navegador erraria o prazo pra quem está em outro fuso.
 */
export function parsePixExpiration(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})$/.exec(value.trim());
  const ms = match ? Date.parse(`${match[1]}T${match[2]}-03:00`) : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
  return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
}

/** QR do Asaas chega em base64 puro; às vezes já como data URI. */
export function toQrSrc(image: string | null | undefined): string | null {
  if (!image) return null;
  return image.startsWith('data:') ? image : `data:image/png;base64,${image}`;
}

export function safeUrl(path: string, base: string): string {
  try {
    return new URL(path, base).toString();
  } catch {
    return new URL(path, RESERVEI_FALLBACK_URL).toString();
  }
}

/**
 * Computador × celular/tablet. O desktop não tem a barra fixa de baixo (o resumo
 * já está sempre à vista) e põe o botão principal no pé do card.
 */
export function useIsDesktop(): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DESKTOP_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(DESKTOP_QUERY);
    const update = (): void => setMatches(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return matches;
}

/** Título da aba + `noindex`: página de pagamento não entra no Google. */
export function useCheckoutPageMeta(title: string): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    const existing = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const robots = existing ?? document.createElement('meta');
    const previousContent = robots.getAttribute('content');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    if (!existing) document.head.appendChild(robots);
    return () => {
      document.title = previousTitle;
      if (existing) {
        if (previousContent === null) existing.removeAttribute('content');
        else existing.content = previousContent;
      } else {
        robots.remove();
      }
    };
  }, [title]);
}

export function Price({ value, suffix }: { value: number; suffix: string }): JSX.Element {
  const [whole = '0', decimal = '00'] = value.toFixed(2).split('.');
  return (
    <span className="reservei-checkout__price">
      <span className="reservei-checkout__price-currency">R$</span>
      <span>{Number(whole).toLocaleString('pt-BR')}</span>
      <span className="reservei-checkout__price-decimal">,{decimal}</span>
      <span className="reservei-checkout__price-suffix">{suffix}</span>
    </span>
  );
}

export function SelectionHighlight({ reducedMotion }: { reducedMotion: boolean }): JSX.Element {
  return (
    <motion.span
      className="reservei-checkout__selection-highlight"
      initial={reducedMotion ? false : { opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={reducedMotion ? { duration: 0 } : { duration: 0.2 }}
      aria-hidden="true"
    />
  );
}

// O miolo do topo e do rodapé tem a MESMA largura do conteúdo (1280 − 2×32):
// calcular a margem pelo 100vw desalinhava logo/“Ajuda” do conteúdo em ~32px
// (e o 100vw ainda conta a barra de rolagem no Windows).
export function Header({ homeUrl, backTarget, helpUrl }: { homeUrl: string; backTarget: string; helpUrl: string }): JSX.Element {
  return (
    <header className="reservei-checkout__header">
      <div className="reservei-checkout__header-inner">
        <a className="reservei-checkout__brand" href={homeUrl} aria-label="Reservei">
          <img src="/checkout/reservei/logo-tight.png" alt="" />
          <span>eservei</span>
        </a>
        <div className="reservei-checkout__header-actions">
          <a className="reservei-checkout__back-link" href={backTarget}>
            Voltar ao Reservei
          </a>
          <a className="reservei-checkout__help reservei-checkout__help--desktop" href={helpUrl} target="_blank" rel="noopener noreferrer">
            <CircleHelp size={19} aria-hidden="true" />
            Ajuda
          </a>
          <a className="reservei-checkout__help reservei-checkout__help--mobile" href={helpUrl} target="_blank" rel="noopener noreferrer">
            Ajuda
          </a>
        </div>
      </div>
    </header>
  );
}

export function Footer(): JSX.Element {
  return (
    <footer className="reservei-checkout__footer">
      <div className="reservei-checkout__footer-inner">
        <span>Reservei</span>
        <span>Pagamento processado com segurança pelo Asaas</span>
      </div>
    </footer>
  );
}

export function FieldError({ id, message }: { id: string; message: string }): JSX.Element | null {
  if (!message) return null;
  return (
    <span className="reservei-checkout__error" id={id}>
      {message}
    </span>
  );
}

interface ShellLinks {
  homeUrl: string;
  backTarget: string;
  helpUrl: string;
}

/** Tela que encerra o caminho aqui (erro, link vencido, nada a fazer). */
export function BlockedScreen({
  links,
  title,
  message,
  action,
}: {
  links: ShellLinks;
  title: string;
  message: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="reservei-checkout">
      <Header {...links} />
      <main className="reservei-checkout__main">
        <section className="reservei-checkout__blocked" aria-labelledby="rc-blocked-title">
          <h1 id="rc-blocked-title">{title}</h1>
          <p>{message}</p>
          {action ?? (
            <a className="reservei-checkout__primary-link" href={links.backTarget}>
              Voltar ao Reservei
              <ArrowRight size={18} aria-hidden="true" />
            </a>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function LoadingScreen({ links, label }: { links: ShellLinks; label: string }): JSX.Element {
  return (
    <div className="reservei-checkout">
      <Header {...links} />
      <main className="reservei-checkout__main">
        <div className="reservei-checkout__loading" role="status" aria-label={label}>
          <span className="reservei-checkout__skeleton reservei-checkout__skeleton--title" />
          <span className="reservei-checkout__skeleton" />
          <span className="reservei-checkout__skeleton reservei-checkout__skeleton--block" />
        </div>
      </main>
      <Footer />
    </div>
  );
}

/** Marca de sucesso da prévia (check + anel + raios, uma vez só). */
export function SuccessMark(): JSX.Element {
  return (
    <span className="reservei-checkout__success-mark" aria-hidden="true">
      <svg className="reservei-checkout__success-check" viewBox="0 0 64 64" focusable="false">
        <circle className="reservei-checkout__success-ring" cx="32" cy="32" r="27" />
        <path className="reservei-checkout__success-check-path" d="M19 33.5 28 42l17-20" />
      </svg>
      {(['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight'] as const).map((ray) => (
        <span key={ray} className={`reservei-checkout__success-ray reservei-checkout__success-ray--${ray}`} />
      ))}
    </span>
  );
}

/** Copiar o código Pix: ignora respostas de cliques antigos (o último vale). */
export function useCopyCode(): { state: 'idle' | 'copied' | 'failed'; copy: (code: string) => Promise<void> } {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const requestRef = useRef(0);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  const copy = async (code: string): Promise<void> => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    try {
      if (!clipboard?.writeText) throw new Error('clipboard indisponível');
      await clipboard.writeText(code);
      if (aliveRef.current && requestRef.current === requestId) setState('copied');
    } catch {
      if (aliveRef.current && requestRef.current === requestId) setState('failed');
    }
  };
  return { state, copy };
}

/**
 * Copia-e-cola em UMA linha (cortada com "…") + botão de copiar ao lado.
 * Ninguém lê o código — a caixa de 3 linhas só ocupava a tela (pedido do
 * Wesley, 20/10). Se o navegador bloquear a cópia, tocar no campo seleciona o
 * código inteiro pra copiar à mão.
 */
export function PixCodeField({
  payload,
  copyState,
  onCopy,
}: {
  payload: string;
  copyState: 'idle' | 'copied' | 'failed';
  onCopy: () => void;
}): JSX.Element {
  const copied = copyState === 'copied';
  return (
    <div className="reservei-checkout__pix-code-field">
      <label htmlFor="rc-pix-code">Código Pix copia e cola</label>
      <div className="reservei-checkout__pix-code-row">
        <input
          id="rc-pix-code"
          type="text"
          readOnly
          value={payload}
          spellCheck={false}
          onFocus={(event) => event.currentTarget.select()}
        />
        <button
          type="button"
          className="reservei-checkout__pix-copy"
          aria-label={copied ? 'Código copiado' : 'Copiar código Pix'}
          onClick={onCopy}
        >
          {copied ? <Check size={17} aria-hidden="true" /> : <Copy size={17} aria-hidden="true" />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      {copyState === 'failed' && (
        <span role="status">Não foi possível copiar. Toque no código para selecioná-lo e copie.</span>
      )}
    </div>
  );
}

/**
 * QR + copia-e-cola + valor, no mesmo desenho do Pix da compra. A confirmação
 * (polling) é de quem usa — aqui só a apresentação.
 */
export function PixQrBlock({
  qrSrc,
  payload,
  amount,
  summary,
  expiresAt,
  waitingText,
}: {
  qrSrc: string | null;
  payload: string | null;
  amount: number | null;
  summary: string;
  expiresAt: number | null;
  waitingText: string;
}): JSX.Element {
  const { state: copyState, copy } = useCopyCode();
  return (
    <>
      {qrSrc && (
        <span className="reservei-checkout__pix-qr">
          <img src={qrSrc} alt="QR Code do Pix" />
        </span>
      )}
      {amount !== null && (
        <div className="reservei-checkout__pix-amount">
          <span>Valor a pagar</span>
          <strong>{formatPrice(amount)}</strong>
        </div>
      )}
      {payload && <PixCodeField payload={payload} copyState={copyState} onCopy={() => void copy(payload)} />}
      <div className="reservei-checkout__pix-summary" aria-label="Resumo do Pix">
        <span>{summary}</span>
        {expiresAt !== null && <strong>Vale até {formatBRDateTime(new Date(expiresAt))}</strong>}
      </div>
      <p className="reservei-checkout__pix-instructions">{waitingText}</p>
    </>
  );
}
